import React, { useState, useCallback, useMemo } from 'react';
import { stagePublicAPI } from '../services/api';
import ChampDate from './ChampDate';
import { Piece, tailleMo, tailleLisible, dateFr } from './StageDepotPublic';
import './StagePublic.css';

/**
 * CERTIFICATION — la PAGE DE L'ÉTUDIANT (lot C.2)
 * ================================================
 * Spec     : doc/modules/suivi_de_stage_certification.md § Pages publiques — Le dépôt de l'étudiant
 * Maquette : #tab-certifetudiant
 *
 * ── LE PORTAIL DU STAGE, PARAMÉTRÉ ─────────────────────────────────────────
 * Même identification (numéro + date de naissance, E82), même carte de pièce
 * (`Piece`, exportée par StageDepotPublic), même règle « un fichier part dès qu'il
 * est choisi » (E94). Ce qui s'ajoute : la DÉCLARATION « hors entreprise » (C26)
 * et l'avertissement disciplinaire, et le bleu encre (C36) — c'est une page
 * TRANSMISE, elle se distingue des pages vertes du stage.
 *
 * ── LA DÉCLARATION NAÎT À LA VALIDATION (CD2) ─────────────────────────────
 * Cocher la case est un état de la page, rien de plus : l'étudiant peut encore
 * changer d'avis. Elle part avec « Valider mon dépôt », après un RÉCAPITULATIF ;
 * le serveur l'enregistre avec le texte exact de l'avertissement affiché.
 *
 * ⚠️ `apercu` = un état FICTIF (« Voir la page de l'étudiant », C28) : aucun appel
 * au serveur, un dépôt s'affiche localement, la validation ne part pas.
 */

/** La même règle que le serveur (`raisonNonValidable`) — l'écran l'anticipe, le serveur décide. */
function raisonNonValidable(livrables, decl, correction = false) {
    const declare = (x) => x.hors_entreprise_possible && decl[x.livrable_id]?.coche;
    const doublons = livrables.filter((x) => !declare(x) && x.courante?.alerte);
    if (doublons.length) return `Fichier à remplacer : ${doublons.map((x) => x.libelle).join(', ')} (même fichier pour deux livrables).`;
    // CM3 — en correction : seuls les documents refusés par le maître sont à redéposer.
    if (correction) {
        const aCorriger = livrables.filter((x) => x.etat === 'a_corriger');
        if (aCorriger.length) return `Il reste à corriger : ${aCorriger.map((x) => x.libelle).join(', ')}.`;
        return livrables.some((x) => x.etat === 'corrige') ? null : 'Aucun document corrigé.';
    }
    const sansPrecision = livrables.filter((x) => declare(x) && !String(decl[x.livrable_id]?.precision || '').trim());
    if (sansPrecision.length) return `Précisez le sujet traité pour : ${sansPrecision.map((x) => x.libelle).join(', ')}.`;
    const manquants = livrables.filter((x) => x.obligatoire && !x.courante && !declare(x));
    if (manquants.length) return `Il reste à déposer : ${manquants.map((x) => x.libelle).join(', ')}.`;
    if (!livrables.some((x) => x.courante || declare(x))) return 'Aucun document déposé ni déclaré.';
    return null;
}

/**
 * Un livrable vu comme une « pièce » du portail du stage : c'est la même carte.
 * L'ÉTAT du serveur dit ce qu'elle montre : certifié → acquis (pas de zone) ; à corriger →
 * le motif du maître et une zone de dépôt ; corrigé → la nouvelle version, remplaçable.
 */
const enPiece = (x) => ({
    attendu_id: x.livrable_id, libelle: x.libelle, obligatoire: x.obligatoire, formats: x.formats,
    courante: !x.courante ? null
        : x.etat === 'certifie' ? { statut: 'valide', soumise: true, nom_fichier: x.courante.nom_fichier, taille_octets: x.courante.taille_octets }
        : x.etat === 'a_corriger' ? { statut: 'rejete', soumise: true, motif_rejet: x.motif, nom_fichier: x.courante.nom_fichier, taille_octets: x.courante.taille_octets }
        : { statut: 'depose', soumise: x.courante.soumise, nom_fichier: x.courante.nom_fichier,
            taille_octets: x.courante.taille_octets, alerte: x.courante.alerte }
});

function StageCertifDepotPublic({ token, entete, apercu = null }) {
    const [identite, setIdentite] = useState({ numero: '', dateNaissance: '' });
    const [etat, setEtat] = useState(apercu);
    const [decl, setDecl] = useState({});            // livrable_id → { coche, precision }
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [erreurPiece, setErreurPiece] = useState({});
    const [recap, setRecap] = useState(false);
    const [valide, setValide] = useState(null);
    const d = etat?.dossier || entete?.dossier || {};

    const poserDecl = (id, morceau) => { setRecap(false); setDecl((x) => ({ ...x, [id]: { ...(x[id] || {}), ...morceau } })); };

    const identifier = useCallback(async (e) => {
        e.preventDefault();
        setEnCours(true); setErreur(null);
        try {
            const r = await stagePublicAPI.identifierCertifDepot(token, identite);
            setEtat(r.data);
        } catch (err) {
            setErreur(err.response?.data?.error || 'Identification impossible. Réessayez, ou contactez le secrétariat.');
        } finally { setEnCours(false); }
    }, [token, identite]);

    const deposer = useCallback(async (piece, fichier, refus) => {
        setRecap(false);
        setErreurPiece((x) => ({ ...x, [piece.attendu_id]: null }));
        if (refus) { setErreurPiece((x) => ({ ...x, [piece.attendu_id]: refus })); return; }
        if (fichier.size > etat.taille_max_octets) {
            setErreurPiece((x) => ({ ...x, [piece.attendu_id]:
                `Votre fichier fait ${tailleMo(fichier.size)}, la limite est ${tailleMo(etat.taille_max_octets)} — réduisez-le (export PDF « taille réduite »).` }));
            return;
        }
        if (apercu) {
            setEtat((e) => ({ ...e, livrables: e.livrables.map((x) => (x.livrable_id !== piece.attendu_id ? x
                : { ...x, courante: { soumise: false, nom_fichier: fichier.name, taille_octets: fichier.size, alerte: null } })) }));
            return;
        }
        setEnCours(true);
        try {
            const r = await stagePublicAPI.deposerCertifPiece(token, piece.attendu_id, identite, fichier);
            setEtat(r.data);
        } catch (err) {
            setErreurPiece((x) => ({ ...x, [piece.attendu_id]: err.response?.data?.error || "Le dépôt n'a pas abouti. Réessayez." }));
        } finally { setEnCours(false); }
    }, [apercu, token, identite, etat]);

    const livrables = useMemo(() => etat?.livrables || [], [etat]);
    const declare = (x) => x.hors_entreprise_possible && decl[x.livrable_id]?.coche;
    const correction = !!etat?.correction;
    const raison = etat?.ouvert ? raisonNonValidable(livrables, decl, correction) : null;

    const valider = useCallback(async () => {
        if (apercu) { setValide("Aperçu : rien n'a été envoyé. Dans la vraie page, l'étudiant verrait ici la confirmation, et son maître recevrait son lien."); return; }
        setEnCours(true); setErreur(null);
        try {
            const declarations = livrables.filter((x) => x.hors_entreprise_possible && decl[x.livrable_id]?.coche)
                .map((x) => ({ livrable_id: x.livrable_id, precision: decl[x.livrable_id].precision || '' }));
            const r = await stagePublicAPI.validerCertifDepot(token, identite, declarations);
            setEtat(r.data);
            setValide(r.data.confirmation);
        } catch (err) {
            setRecap(false);
            setErreur(err.response?.data?.error || "La validation n'a pas abouti. Réessayez.");
        } finally { setEnCours(false); }
    }, [apercu, token, identite, livrables, decl]);

    /**
     * Le TITRE suit l'état du dossier (Bertrand, 2026-10-05 : « Documents à faire certifier » était faux
     * une fois tout certifié). Avant l'identification, l'état n'est pas connu : un titre neutre.
     * ⚠️ « Documents certifiés », et non « certificat de conformité » : ce terme a un sens précis ailleurs
     * (produits, marquage CE) et ferait croire à un document officiel de l'IUT — c'est le MAÎTRE qui certifie.
     */
    const termine = etat?.statut === 'termine';
    const [titre, icone] = !etat ? ['Certification des documents', 'fa-stamp']
        : termine ? ['Documents certifiés', 'fa-circle-check']
        : etat.ouvert && etat.correction ? ['Documents à corriger', 'fa-pen-to-square']
        : etat.ouvert ? ['Documents à faire certifier', 'fa-file-arrow-up']
        : ['Documents transmis pour certification', 'fa-paper-plane'];
    const enTete = (
        <div className="form-public-header">
            <h2><i className={'fa-solid ' + icone} /> {titre} — {d.processus_nom}</h2>
            <p><b>{d.nom} {d.prenom}</b>{d.formation_nom ? ` · ${d.formation_nom}` : ''}
               {/* La date limite n'a de sens que tant que le dépôt est ouvert (ou avant l'identification). */}
               {d.date_limite_depot && (!etat || etat.ouvert) ? ` · à déposer avant le ${dateFr(d.date_limite_depot)}` : ''}</p>
            <p>{d.departement_nom}</p>
        </div>
    );

    if (valide) {
        return (
            <div className="pub-page pub-certif" style={{ minHeight: apercu ? 0 : undefined }}>
                <div className="pub-message pub-message-succes">
                    <i className="fa-solid fa-circle-check" />
                    <h2>Dépôt validé</h2>
                    <p>{valide}</p>
                    <p className="pub-discret">Vous recevrez un courriel si un document doit être corrigé. Vous pouvez fermer cette page.</p>
                </div>
            </div>
        );
    }

    // ── L'identification (E82) ──────────────────────────────────────────────
    if (!etat) {
        return (
            <div className="pub-page pub-certif">
                <form className="form-public" onSubmit={identifier}>
                    {enTete}
                    <div className="form-public-body">
                        <div className="form-section">
                            <h3><i className="fa-solid fa-id-card" /> Identifiez-vous</h3>
                            <p className="pub-aide pub-aide-bloc">
                                Pour déposer les documents à faire certifier, confirmez votre identité.
                                Si vous avez reçu ce lien par erreur, ne l'utilisez pas.
                            </p>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="cdep-num">Votre numéro étudiant <span className="form-required">*</span></label>
                                    <input id="cdep-num" value={identite.numero} autoFocus autoComplete="off" placeholder="Ex : 20257566"
                                           onChange={(e) => setIdentite({ ...identite, numero: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="cdep-naiss">Votre date de naissance <span className="form-required">*</span></label>
                                    <ChampDate id="cdep-naiss" value={identite.dateNaissance}
                                           onChange={(e) => setIdentite({ ...identite, dateNaissance: e.target.value })} />
                                </div>
                            </div>
                            {erreur && <div className="pub-encart pub-encart-erreur"><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}
                        </div>
                    </div>
                    <div className="form-public-footer">
                        <button type="submit" className="pub-btn" disabled={enCours || !identite.numero || !identite.dateNaissance}>
                            {enCours ? <><i className="fa-solid fa-circle-notch pub-tourne" /> Vérification…</>
                                     : <><i className="fa-solid fa-arrow-right" /> Accéder à mon dépôt</>}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    const c = etat.certificateur || {};
    const qui = [c.nom, c.entreprise].filter(Boolean).join(', ');

    // ── Le dépôt ────────────────────────────────────────────────────────────
    // ⚠️ Exception à C36 (Bertrand, 2026-10-05) : la page d'un dossier TERMINÉ passe au VERT — le succès.
    return (
        <div className={'pub-page pub-certif' + (termine ? ' pub-certif-termine' : '')}>
            <div className="form-public">
                {enTete}
                <div className="form-public-body">
                    {apercu && (
                        <div className="pub-encart pub-encart-info">
                            <i className="fa-solid fa-eye" /> Aperçu — données fictives. Cette page est celle que reçoit l'étudiant ; rien n'est envoyé.
                        </div>
                    )}
                    {etat.ouvert && correction && (
                        <div className="pub-encart pub-encart-erreur" style={{ marginBottom: 16 }}>
                            <i className="fa-solid fa-pen-to-square" /> Votre {etat.certificateur_role || 'maître'} n'a pas pu certifier certains documents.
                            Déposez une nouvelle version des documents signalés — les autres restent acquis.
                        </div>
                    )}
                    {etat.ouvert && !correction && (
                        <div className="pub-bandeau-certif">
                            Déposez chaque document. Dès que vous validez, votre {etat.certificateur_role || 'maître'}
                            {qui ? ` (${qui})` : ''} reçoit un lien pour les certifier.
                        </div>
                    )}
                    {etat.message && (termine
                        ? <div className="pub-encart pub-encart-certifie" style={{ marginBottom: 16 }}><i className="fa-solid fa-circle-check" /> {etat.message}</div>
                        : <div className="pub-encart pub-encart-info"><i className="fa-solid fa-circle-info" /> {etat.message}</div>)}

                    <div className="form-section">
                        <h3><i className="fa-solid fa-file-lines" /> Vos livrables</h3>
                        {etat.ouvert && (
                            <p className="pub-aide pub-aide-bloc">
                                Chaque document est enregistré dès que vous le choisissez ; vous pouvez le remplacer jusqu'à la validation.
                                Les livrables obligatoires (<span className="form-required">*</span>) sont requis pour valider.
                            </p>
                        )}

                        {livrables.map((x) => {
                            const id = x.livrable_id;
                            const coche = declare(x);
                            // Après la validation : ce qui a été déposé, ou déclaré — en lecture.
                            if (!etat.ouvert) {
                                return (
                                    <div key={id} className="pub-piece">
                                        <div className="pub-piece-tete"><strong>{x.libelle}</strong></div>
                                        {x.declaration ? (
                                            <div className="pub-piece-fichier">
                                                <i className="fa-solid fa-graduation-cap" /> Déclaré hors entreprise le {dateFr(x.declaration.declare_le)} :
                                                {' '}« {x.declaration.precision} »
                                            </div>
                                        ) : x.courante ? (
                                            <>
                                                <div className="pub-piece-fichier">
                                                    <i className="fa-solid fa-file-lines" /> {x.courante.nom_fichier}
                                                    <span className="pub-discret"> · {tailleLisible(x.courante.taille_octets)}</span>
                                                </div>
                                                {x.certifie_texte && (
                                                    <div className="pub-certifie-par"><i className="fa-solid fa-circle-check" /> {x.certifie_texte}</div>
                                                )}
                                            </>
                                        ) : <div className="pub-discret">Rien de déposé.</div>}
                                    </div>
                                );
                            }
                            // En correction, une déclaration déjà faite se RELIT, elle ne se refait pas.
                            if (x.etat === 'declare') {
                                return (
                                    <div key={id} className="pub-piece">
                                        <div className="pub-piece-tete"><strong>{x.libelle}</strong></div>
                                        <div className="pub-piece-fichier">
                                            <i className="fa-solid fa-graduation-cap" /> Déclaré hors entreprise le {dateFr(x.declaration?.declare_le)} :
                                            {' '}« {x.declaration?.precision} »
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <div key={id}>
                                    {x.etat === 'corrige' && x.motif && (
                                        <div className="pub-encart pub-encart-erreur" style={{ marginBottom: 6 }}>
                                            <i className="fa-solid fa-circle-exclamation" /> <b>{x.libelle} :</b> {x.motif}
                                        </div>
                                    )}
                                    {!coche && (
                                        <Piece piece={enPiece(x)} ouvert={etat.ouvert} tailleMax={etat.taille_max_octets}
                                               enCours={enCours} onFichier={deposer} libelleAcquise="Certifié" />
                                    )}
                                    {x.certifie_texte && (
                                        <div className="pub-certifie-par" style={{ margin: '-6px 0 12px' }}><i className="fa-solid fa-circle-check" /> {x.certifie_texte}</div>
                                    )}
                                    {erreurPiece[id] && !coche && (
                                        <div className="pub-encart pub-encart-erreur"><i className="fa-solid fa-circle-exclamation" /> {erreurPiece[id]}</div>
                                    )}

                                    {x.hors_entreprise_possible && (
                                        <div className={coche ? 'pub-declaration' : ''} style={coche ? undefined : { margin: '-6px 0 14px' }}>
                                            {/* Cochée : UNE carte bordée de rouge, le titre du livrable dedans (maquette). */}
                                            {coche && (
                                                <div className="pub-piece-tete">
                                                    <strong>{x.libelle}</strong>
                                                    {x.obligatoire ? <span className="form-required"> *</span> : <span className="pub-discret"> (facultatif)</span>}
                                                    <span className="pub-discret"> · déclaré hors entreprise</span>
                                                </div>
                                            )}
                                            <label className="pub-declaration-case">
                                                <input type="checkbox" checked={!!coche} disabled={enCours}
                                                       onChange={(e) => poserDecl(id, { coche: e.target.checked })} />
                                                <span>
                                                    J'ai traité le sujet proposé par la formation (hors entreprise) : je n'ai pas de document à faire
                                                    certifier par mon {etat.certificateur_role || 'maître'} pour ce livrable.
                                                </span>
                                            </label>
                                            {coche && (
                                                <>
                                                    <div className="form-group" style={{ margin: '10px 0 0' }}>
                                                        <label htmlFor={'cdep-prec-' + id}>Précisez le sujet traité <span className="form-required">*</span></label>
                                                        <input id={'cdep-prec-' + id} maxLength={500} value={decl[id]?.precision || ''}
                                                               placeholder="Ex : amélioration du flux de la cellule d'assemblage — sujet de M. Martin"
                                                               onChange={(e) => poserDecl(id, { precision: e.target.value })} />
                                                    </div>
                                                    <div className="pub-avertissement" role="alert">
                                                        <i className="fa-solid fa-triangle-exclamation" />
                                                        <div>
                                                            <div className="pub-avertissement-titre">{etat.avertissement}</div>
                                                            <div style={{ fontSize: 12 }}>
                                                                Si ce livrable a été réalisé, même en partie, dans votre entreprise, ne cochez pas cette case :
                                                                déposez-le, votre {etat.certificateur_role || 'maître'} le certifiera. Votre déclaration est
                                                                enregistrée, datée, et peut être contrôlée.
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {x.courante && (
                                                        <div className="pub-discret" style={{ marginTop: 8 }}>
                                                            <i className="fa-solid fa-circle-info" /> Le fichier déjà choisi pour ce livrable ({x.courante.nom_fichier})
                                                            ne sera pas transmis.
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Le RÉCAPITULATIF avant l'acte (règle 6) : ce qui part, et ce qui est déclaré. */}
                    {recap && !raison && (
                        <div className="pub-recap">
                            <b>Vous allez valider votre dépôt.</b> Il ne sera plus modifiable.
                            <ul>
                                {livrables.filter((x) => !declare(x) && x.courante && (!correction || x.etat === 'corrige')).map((x) => (
                                    <li key={x.livrable_id}>{x.libelle} : <b>{x.courante.nom_fichier}</b> — transmis à votre {etat.certificateur_role || 'maître'}</li>
                                ))}
                                {livrables.filter(declare).map((x) => (
                                    <li key={x.livrable_id}>{x.libelle} : <b>déclaré hors entreprise</b> — « {decl[x.livrable_id]?.precision} »</li>
                                ))}
                            </ul>
                            {livrables.some(declare) && (
                                <div style={{ color: '#991b1b', fontWeight: 600, marginTop: 8 }}>
                                    <i className="fa-solid fa-triangle-exclamation" /> {etat.avertissement}
                                </div>
                            )}
                        </div>
                    )}
                    {erreur && <div className="pub-encart pub-encart-erreur"><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}
                </div>

                {etat.ouvert && (
                    <div className="form-public-footer pub-pied-depot">
                        {raison && <div className="pub-aide pub-raison"><i className="fa-solid fa-circle-exclamation" /> {raison}</div>}
                        {recap && !raison ? (
                            <>
                                <button type="button" className="pub-btn pub-btn-secondaire" disabled={enCours} onClick={() => setRecap(false)}>
                                    Revenir
                                </button>
                                <button type="button" className="pub-btn" disabled={enCours} onClick={valider}>
                                    {enCours ? <><i className="fa-solid fa-circle-notch pub-tourne" /> Validation…</>
                                             : <><i className="fa-solid fa-check" /> Confirmer la validation</>}
                                </button>
                            </>
                        ) : (
                            <button type="button" className="pub-btn" disabled={enCours || !!raison} onClick={() => setRecap(true)}>
                                <i className="fa-solid fa-check" /> Valider mon dépôt
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default StageCertifDepotPublic;
